/**
 * HEIC / HEIF decode per IMAGE_EDITOR_V2.md §4 and R10. There is no dependable
 * browser HEIC *encoder*, so `encodeFallback('heic') === 'jpg'` and this file
 * has no encode half.
 *
 * We decode with libheif's inlined-wasm bundle loaded as a CLASSIC <script>
 * (`/js/lib/libheif-bundle.js`, copied from `libheif-js/libheif-wasm/`), exactly
 * as the app already loads UTIF and glfx. This deliberately bypasses the module
 * bundler: shipping libheif's Emscripten glue through Next/webpack left the
 * module's `ready` promise unresolved and hung the editor on "opening…" (found
 * on QA 2026-09-06). A plain script tag runs the glue in its own scope and
 * exposes `window.libheif`; the wasm is base64-inlined, so there is no fetch to
 * misresolve. browser-only, covered by the QA matrix in the doc (§6).
 */
import { codecError, readBytes } from './canvas-utils';

const SRC = '/js/lib/libheif-bundle.js';
const CANNOT_OPEN = 'This browser could not open the HEIC file. Convert it to JPG with the HEIC converter first.';

let scriptPromise: Promise<void> | null = null;
let modulePromise: Promise<any> | null = null;

/** Inject the classic <script> once; resolves when `window.libheif` (a factory) exists. */
function loadScript(): Promise<void> {
    if (typeof window !== 'undefined' && (window as any).libheif) return Promise.resolve();
    if (scriptPromise) return scriptPromise;
    scriptPromise = new Promise<void>((resolve, reject) => {
        if (typeof document === 'undefined') { reject(new Error(CANNOT_OPEN)); return; }
        const done = () => (window as any).libheif ? resolve() : reject(new Error(CANNOT_OPEN));
        const existing = document.querySelector<HTMLScriptElement>(`script[src="${SRC}"]`);
        if (existing) { existing.addEventListener('load', done); existing.addEventListener('error', () => reject(new Error(CANNOT_OPEN))); if ((window as any).libheif) resolve(); return; }
        const node = document.createElement('script');
        node.src = SRC; node.async = true;
        node.onload = done;
        node.onerror = () => { scriptPromise = null; reject(new Error(CANNOT_OPEN)); };
        document.head.appendChild(node);
    });
    return scriptPromise;
}

/**
 * `window.libheif` from the bundle is a FACTORY (the same value `require('libheif-js')`
 * exposes, which `libheif-js/wasm-bundle` calls once). Call it, await its Emscripten
 * `ready`, and cache the initialised module (HeifDecoder lives on it).
 */
function loadLibheif(): Promise<any> {
    if (modulePromise) return modulePromise;
    modulePromise = loadScript().then(async () => {
        const factory = (window as any).libheif;
        const mod = typeof factory === 'function' ? await factory() : factory;
        if (mod && mod.ready) await mod.ready;
        if (!mod || typeof mod.HeifDecoder !== 'function') throw new Error('libheif loaded but exposed no HeifDecoder');
        return mod;
    }).catch(err => { modulePromise = null; throw err; });
    return modulePromise;
}

/**
 * HEIC file → a canvas. Picks the primary (first) image of a multi-image file.
 * Replicates heic-decode's `image.display` step against the raw libheif bundle
 * so we do not depend on heic-decode's own `require('libheif-js')` (the path
 * that broke under webpack).
 */
export async function decodeHeic(file: Blob): Promise<HTMLCanvasElement> {
    let libheif: any;
    try {
        libheif = await loadLibheif();
    } catch (err) {
        throw codecError(CANNOT_OPEN, err);
    }
    try {
        const buffer = await readBytes(file);
        const decoder = new libheif.HeifDecoder();
        const images = decoder.decode(buffer);
        if (!images || !images.length) throw new Error('no images in HEIF container');
        const image = images[0];
        const width = image.get_width();
        const height = image.get_height();
        if (!width || !height) throw new Error('zero-sized HEIF image');
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('no 2d context');
        const imageData = ctx.createImageData(width, height);
        await new Promise<void>((resolve, reject) => {
            image.display(imageData, (displayData: any) => {
                if (!displayData) { reject(new Error('HEIF display error')); return; }
                ctx.putImageData(displayData instanceof ImageData ? displayData : imageData, 0, 0);
                resolve();
            });
        });
        // free native memory where the binding exposes it
        try { images.forEach((im: any) => im.free && im.free()); } catch { /* older bindings have no free */ }
        return canvas;
    } catch (err) {
        throw codecError(CANNOT_OPEN, err);
    }
}
